import type { ConfigurationOption, ConfigurationSelections, ConfigurationSet } from './configurationSelection'
import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import Select from 'react-select'
import { Button, Row } from 'reactstrap'
import { indefinite } from '../../util/utils'
import dollar_price from '../general/DollarPrice'
import { configurableSelectStyles } from './configurableSelectStyles'
import './ProductVariantSelect.css'

type ProductVariantSelectProps = {
  product: { sku: string; variants: ConfigurationSet[] }
  selectedOptionsByVariantId: ConfigurationSelections
  selectVariant: (optionId: number, variant: ConfigurationSet) => void
  availableOptions: { allAvailable: boolean } & Record<number, Set<string>>
  currentLocation: { show_price: boolean }
  isLoading: boolean
  stocks: Record<string, number>
  buildSkuSuffix: (selections: ConfigurationSelections) => string
  isBundle?: boolean
}

const ProductVariantSelect = (props: ProductVariantSelectProps) => {
  const { buildSkuSuffix, product, selectedOptionsByVariantId, stocks } = props
  const variants = product.variants

  const extraPrice = (cents: number) => {
    const value = dollar_price(cents)
    if (cents > 0) {
      return <> + {value}</>
    } else if (cents < 0) {
      return <> - {value}</>
    } else {
      return ''
    }
  }

  // returns true if user has selected an option for a given variant id
  const selectionMade = (variant_id: number) => !!selectedOptionsByVariantId[variant_id]

  const getOptionExtraPrice = (option: ConfigurationOption) => {
    const priceModifierData = option.price_modifier
    if (priceModifierData) {
      return extraPrice(priceModifierData.price_modifier_cents)
    }
    return null
  }

  const isOptionAvailable = (variant: ConfigurationSet, option: ConfigurationOption) => {
    if (props.availableOptions.allAvailable) {
      return true
    }
    return props.availableOptions[variant.variant_id]?.has(option.sku_code)
  }

  // sort Swatch options by disabled/enabled states
  const sortedVariants = useMemo(() => {
    const variantOptions: Record<number, ConfigurationOption[]> = {}
    variants.forEach(variant => {
      if (variant.display_type === 'Swatch') {
        variantOptions[variant.variant_id] = [
          ...variant.nested_data.filter(opt => props.availableOptions[variant.variant_id]?.has(opt.sku_code)),
          ...variant.nested_data.filter(opt => !props.availableOptions[variant.variant_id]?.has(opt.sku_code))
        ]
        return
      }
      variantOptions[variant.variant_id] = [...variant.nested_data]
    })
    return variantOptions
  }, [variants, props.availableOptions])

  const getNotAvailableSuffix = useCallback(
    (variantId: number, option: ConfigurationOption) => {
      const selectedOptions = {
        ...selectedOptionsByVariantId,
        [variantId]: option
      }
      const fullSku = `${product.sku}${buildSkuSuffix(selectedOptions)}`

      if (Object.prototype.hasOwnProperty.call(stocks, fullSku)) {
        return ' - Out of Stock'
      }
      return ' - Not Available'
    },
    [buildSkuSuffix, product, selectedOptionsByVariantId, stocks]
  )

  const getOptionClass = (isSelected: boolean, isSwatch: boolean, isAvailable: boolean) => {
    let baseClass = isSwatch ? 'is-swatch' : 'is-button'
    if (isSelected) {
      baseClass += isSwatch ? ' selected-variant-is-swatch' : ' selected-variant-is-button'
    }
    if (!isAvailable) {
      baseClass += ' disabled'
    }
    return baseClass
  }

  const formatSelectOptionLabel = (
    selectOption: { isAvailable: boolean; option: ConfigurationOption },
    variant: ConfigurationSet
  ) => {
    const { isAvailable, option } = selectOption
    let extraPrice = null
    if (option.has_price_modifier === true) {
      extraPrice = getOptionExtraPrice(option)
    }

    return (
      <>
        {option.name}
        {props.currentLocation.show_price ? extraPrice : null}
        {!isAvailable ? getNotAvailableSuffix(variant.variant_id, option) : null}
      </>
    )
  }

  return (
    <div>
      {variants.map((variant, i) => {
        let extraPrice
        const isSwatch = variant.display_type === 'Swatch'
        const selectedOption = selectedOptionsByVariantId[variant.variant_id]
        if (selectedOption && selectedOption.has_price_modifier === true) {
          extraPrice = getOptionExtraPrice(selectedOption)
        }

        // for variants using buttons/swatch
        if (variant.display_type === 'Button' || isSwatch) {
          return (
            <Row className="variant-options p0 mf-ml-0 mf-mb-20" key={i}>
              <div className="config-option mf-ml-0 w-100">
                <div>
                  {!selectionMade(variant.variant_id) ? (
                    <b>Please select {indefinite(variant.display_name ? variant.display_name : variant.name)}</b>
                  ) : (
                    <>
                      <b>{variant.display_name ? variant.display_name : variant.name}: </b>
                      {selectedOption.name}
                      {props.currentLocation.show_price ? extraPrice : null}
                    </>
                  )}
                </div>

                {sortedVariants[variant.variant_id].map((option, index) => {
                  const isAvailable = isOptionAvailable(variant, option)
                  const isVariantOptionSelected = () => {
                    if (selectedOptionsByVariantId[variant.variant_id]) {
                      return selectedOptionsByVariantId[variant.variant_id].id === option.id
                    } else {
                      return false
                    }
                  }
                  const isSelected = isVariantOptionSelected()

                  let conditionalStyle = {}
                  // modify background color if hex is white
                  const patt = /#*[fF]{3,6}/gm
                  if (patt.test(option.css_hex)) {
                    conditionalStyle = {
                      border: '2px solid #cccccc'
                    }
                  } else {
                    conditionalStyle = isSwatch
                      ? {
                          border: !isAvailable ? '1px solid white' : '2px solid #ffffff'
                        }
                      : {}
                  }

                  return (
                    <React.Fragment key={index}>
                      {isSwatch && !isAvailable && isSelected ? <div className="circle" /> : null}
                      <Button
                        disabled={props.isLoading}
                        id={String(option.id)}
                        value={String(option.id)}
                        onClick={() => props.selectVariant(option.id, variant)}
                        fontSize="50%"
                        className={getOptionClass(isSelected, isSwatch, isAvailable)}
                        style={{
                          color: option.css_hex ? option.css_hex : '#333333',
                          backgroundColor: isSwatch ? option.css_hex : 'rgb(170 168 168 / 34%)',
                          backgroundImage: option.icon?.thumb?.url ? `url(${option.icon?.thumb?.url})` : null,
                          textDecoration: 'none',
                          textAlign: 'center',
                          display: 'inline-block',
                          margin: isSwatch && !isAvailable ? '8px 9px 7px 3px' : '5px 6px 5px 0px',
                          padding: 'inherit',
                          opacity: !isAvailable || props.isLoading ? 0.5 : 1,
                          ...(isSwatch && !isAvailable
                            ? {
                                outline: 'solid rgb(162 162 162) 2px',
                                width: '30px',
                                height: '30px'
                              }
                            : {}),
                          ...(!isAvailable && isSelected ? { boxShadow: 'unset' } : {}),
                          ...conditionalStyle
                        }}
                      >
                        {variant.display_type === 'Button' ? option.name : ''}
                        {!isAvailable ? (
                          <div className={isSwatch ? 'swatch-diagonal-line' : 'button-diagonal-line'} />
                        ) : null}
                      </Button>
                    </React.Fragment>
                  )
                })}
              </div>
            </Row>
          )
        } else {
          const selectOptions = sortedVariants[variant.variant_id].map(option => ({
            value: option.id,
            label: option.name,
            option,
            isAvailable: isOptionAvailable(variant, option)
          }))
          const selectedSelectOption = selectedOption
            ? selectOptions.find(selectOption => selectOption.value === selectedOption.id)
            : null

          return (
            <div key={i} className="config-option configurable-select-option mf-ml-0 mf-mb-20">
              <b>{variant.display_name ? variant.display_name : variant.name}</b>
              <Select
                inputId={`${variant.variant_id}`}
                className="w-100 configurable-option-select"
                classNamePrefix="configurable-option-select"
                isDisabled={props.isLoading}
                isClearable={false}
                options={selectOptions}
                value={selectedSelectOption || null}
                placeholder="Please Select"
                onChange={selectOption => selectOption && props.selectVariant(selectOption.value, variant)}
                formatOptionLabel={selectOption => formatSelectOptionLabel(selectOption, variant)}
                styles={configurableSelectStyles}
              />
            </div>
          )
        }
      })}
      <hr className="w-100 mf-ml-0" />
    </div>
  )
}

const mapStateToProps = state => {
  return {
    currentLocation: state.storeViews.current,
    isLoading: state.productPage.inventoryLoading,
    stocks: state.stocks
  }
}

export default connect(mapStateToProps)(ProductVariantSelect)
